import { observable, type ObservableMap } from 'mobx';

import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import type { WidgetEntity } from 'src/entities/widget/WidgetEntity';

export interface IWidgetStoreService {
  setStore<T extends WidgetEntity, S extends IWidgetStore>(widget: T, store: S): void;
  getStore<T extends WidgetEntity, S extends IWidgetStore>(widget: T): S | null;
  removeStore(widget: WidgetEntity): void;
}

/**
 * Runtime widget stores keyed by entity instance (object identity).
 * Observable so workspace serialization (e.g. reference-wells snapshot via getStore)
 * re-runs when a store is registered or when observable state inside it changes
 * while being read during serialize.
 *
 * Keyed by entity reference (not uuid) so remount with a new entity instance
 * after page navigation creates a fresh store instead of reusing a stale one.
 */
export class WidgetStoreService implements IWidgetStoreService {
  private readonly collection: ObservableMap<WidgetEntity, object> = observable.map();

  setStore<T extends WidgetEntity, S>(widget: T, store: S): void {
    if (this.collection.has(widget)) {
      const errorMessage = 'store is already created';

      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    this.collection.set(widget, store as object);
  }

  getStore<T extends WidgetEntity, S>(widget: T): S | null {
    const store = this.collection.get(widget);

    return (store as S | undefined) ?? null;
  }

  removeStore(widget: WidgetEntity): void {
    this.collection.delete(widget);
  }
}
